import { TextButtonLink } from '@creditornot/cb-button';
import { InputCheckbox } from '@creditornot/cb-checkbox';
import { WindowNew } from '@creditornot/cb-icons';
import { cssThemedValue, colors, WoltLogo, typographyCss } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { localize } from 'common/utils';

type Props = {
  venues: Venue[];
  selectedVenueIds: string[];
  onSelectRows: (venueId: string[]) => void;
};

const Root = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const BaseRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;
  padding: 8px 16px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const HeaderRow = styled(BaseRow)`
  ${typographyCss.Overline3};
  color: ${cssThemedValue('textSecondary')};
`;

const VenueRow = styled(BaseRow)<{ checked: boolean }>`
  ${typographyCss.Body3};
  background-color: ${({ checked }) => (checked ? colors.wolt4 : 'transparent')};
`;

const StyledCheckbox = styled(InputCheckbox)`
  margin-inline-end: 16px;
`;

const VenueBulkSelector = styled.div`
  display: flex;
  align-items: center;
`;

const RowContent = styled.div`
  display: flex;
  align-items: center;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background-color: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
  overflow: hidden;
`;

const Thumbnail = styled.img`
  width: 32px;
  height: 32px;
  object-fit: cover;
`;

const ImageName = styled.div`
  flex: 1;
  margin-inline-start: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlaceholderContainer = styled(ImageContainer)`
  border: 1px solid ${cssThemedValue('borderPrimary')};
`;

const PlaceholderImage = styled(WoltLogo)`
  width: 18px;
  fill: ${cssThemedValue('textTertiary')};
`;

const StyledLinkButton = styled(TextButtonLink)`
  margin-inline-end: 8px;
  display: none;
  cursor: pointer;
  ${VenueRow}:hover & {
    display: block;
  }
`;

const VenuesTable = ({ venues, selectedVenueIds, onSelectRows }: Props) => {
  const intl = useIntl();

  const activeVenues = useMemo(() => venues.filter(venue => venue.status === 'VENUE_PUBLISHED'), [venues]);
  const inactiveVenues = useMemo(() => venues.filter(venue => venue.status !== 'VENUE_PUBLISHED'), [venues]);

  const selectedActiveVenueIds = useMemo(
    () => activeVenues.filter(venue => selectedVenueIds.includes(venue.id)).map(venue => venue.id),
    [activeVenues, selectedVenueIds],
  );
  const selectedInactiveVenueIds = useMemo(
    () => inactiveVenues.filter(venue => selectedVenueIds.includes(venue.id)).map(venue => venue.id),
    [inactiveVenues, selectedVenueIds],
  );

  const onChangeVenueCheckBox = (venueId: string, checked: boolean) => {
    if (checked) {
      onSelectRows([...selectedVenueIds, venueId]);
    } else {
      onSelectRows(selectedVenueIds.filter(id => venueId !== id));
    }
  };

  return (
    <Root>
      {activeVenues.length > 0 && (
        <>
          <HeaderRow>
            <VenueBulkSelector>
              <StyledCheckbox
                checked={selectedActiveVenueIds.length > 0 && selectedActiveVenueIds.length === activeVenues.length}
                partiallyChecked={
                  selectedActiveVenueIds.length > 0 && selectedActiveVenueIds.length < activeVenues.length
                }
                onChange={event => {
                  if (event.currentTarget.checked) {
                    const allActiveVenueIds = activeVenues.map(record => record.id);
                    onSelectRows([...allActiveVenueIds, ...selectedInactiveVenueIds]);
                  } else {
                    onSelectRows(selectedInactiveVenueIds);
                  }
                }}
              />
              <FormattedMessage id="purchases.merchant.active-venues" />
            </VenueBulkSelector>
            <FormattedMessage
              id="purchases.merchant.fraction-selected"
              values={{ fraction: `${selectedActiveVenueIds.length}/${activeVenues.length}` }}
            />
          </HeaderRow>
          {activeVenues.map(venue => {
            const venueName = localize(venue.name, intl.locale);
            const checked = selectedVenueIds.includes(venue.id);
            return (
              <VenueRow key={venue.id} checked={checked}>
                <RowContent>
                  <StyledCheckbox
                    checked={checked}
                    onChange={event => onChangeVenueCheckBox(venue.id, event.currentTarget.checked)}
                  />
                  {venue.listimage ? (
                    <ImageContainer>
                      <Thumbnail src={venue.listimage} />
                    </ImageContainer>
                  ) : (
                    <PlaceholderContainer>
                      <PlaceholderImage />
                    </PlaceholderContainer>
                  )}
                  <ImageName title={venueName}>{venueName}</ImageName>
                </RowContent>
                <RowContent>
                  <StyledLinkButton
                    variant="blue"
                    size="medium"
                    icon={<WindowNew />}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`/venue/${venue.slug}/purchases-history`}
                  />
                  <Tag variant="lightBlue" uppercase>
                    <FormattedMessage id="purchases.merchant.status-active" />
                  </Tag>
                </RowContent>
              </VenueRow>
            );
          })}
        </>
      )}

      {inactiveVenues.length > 0 && (
        <>
          <HeaderRow>
            <VenueBulkSelector>
              <StyledCheckbox
                checked={
                  selectedInactiveVenueIds.length > 0 && selectedInactiveVenueIds.length === inactiveVenues.length
                }
                partiallyChecked={
                  selectedInactiveVenueIds.length > 0 && selectedInactiveVenueIds.length < inactiveVenues.length
                }
                onChange={event => {
                  if (event.currentTarget.checked) {
                    const allInactiveVenueIds = inactiveVenues.map(record => record.id);
                    onSelectRows([...selectedActiveVenueIds, ...allInactiveVenueIds]);
                  } else {
                    onSelectRows(selectedActiveVenueIds);
                  }
                }}
              />
              <FormattedMessage id="purchases.merchant.inactive-venues" />
            </VenueBulkSelector>
            <FormattedMessage
              id="purchases.merchant.fraction-selected"
              values={{ fraction: `${selectedInactiveVenueIds.length}/${inactiveVenues.length}` }}
            />
          </HeaderRow>
          {inactiveVenues.map(venue => {
            const venueName = localize(venue.name, intl.locale);
            const checked = selectedVenueIds.includes(venue.id);
            return (
              <VenueRow key={venue.id} checked={checked}>
                <RowContent>
                  <StyledCheckbox
                    checked={checked}
                    onChange={event => onChangeVenueCheckBox(venue.id, event.currentTarget.checked)}
                  />
                  {venue.listimage ? (
                    <ImageContainer>
                      <Thumbnail src={venue.listimage} />
                    </ImageContainer>
                  ) : (
                    <PlaceholderContainer>
                      <PlaceholderImage />
                    </PlaceholderContainer>
                  )}
                  <ImageName title={venueName}>{venueName}</ImageName>
                </RowContent>
                <RowContent>
                  <StyledLinkButton
                    variant="blue"
                    size="medium"
                    icon={<WindowNew />}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`/venue/${venue.slug}/purchases-history`}
                  />
                  <Tag variant="lightRed" uppercase>
                    <FormattedMessage id="purchases.merchant.status-inactive" />
                  </Tag>
                </RowContent>
              </VenueRow>
            );
          })}
        </>
      )}
    </Root>
  );
};

export default VenuesTable;
